import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Radar } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1663540275466-f171aa6d9f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxyZXNpZGVudGlhbCUyMGNvbW11bml0eSUyMGFlcmlhbHxlbnwwfHx8fDE3OTAzNDYwNzh8MA&ixlib=rb-4.1.0&q=85";

const RADAR_DATA = {
  "Manufactured Housing": { occupancy: "94.2%", delinquency: "6.1%", makeReady: "14 days", training: "81%", violations: 23 },
  Multifamily: { occupancy: "91.7%", delinquency: "8.4%", makeReady: "19 days", training: "64%", violations: 41 },
  "RV Resort": { occupancy: "88.9%", delinquency: "3.2%", makeReady: "6 days", training: "92%", violations: 9 },
  "Single-Family": { occupancy: "95.8%", delinquency: "4.5%", makeReady: "11 days", training: "70%", violations: 15 },
  "Self Storage": { occupancy: "89.5%", delinquency: "5.2%", makeReady: "2 days", training: "68%", violations: 7 },
  HOA: { occupancy: "98.3%", delinquency: "4.9%", makeReady: "n/a", training: "59%", violations: 52 },
};

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

const StatBar = ({ label, value, pct, accent }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-sm font-mono font-semibold text-slate-900">{value}</span>
    </div>
    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
      <motion.div
        key={label + value}
        className={`h-full rounded-full ${accent ? "bg-cadmium" : "bg-moss"}`}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const [ptype, setPtype] = useState("Manufactured Housing");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(my, { stiffness: 120, damping: 18 });
  const ry = useSpring(mx, { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };

  const d = RADAR_DATA[ptype];

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
      <motion.div style={{ y: imgY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute right-0 top-0 h-full w-full object-cover opacity-25 [mask-image:linear-gradient(to_left,black_10%,transparent_75%)]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-[#EDF4FB]" />
      </motion.div>
      <div className="absolute -left-40 top-1/3 opacity-[0.06] pointer-events-none" aria-hidden="true">
        <LogoMark size={520} className="animate-spin-slow" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="eyebrow mb-6 flex items-center gap-3"
          >
            <span className="inline-block h-px w-10 bg-cadmium" />
            The Operations &amp; Training OS for Community Portfolios
          </motion.p>
          <h1
            data-testid="hero-heading-reveal"
            className="font-cabinet text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-slate-900"
          >
            <RevealLine delay={0.25}>Your property software</RevealLine>
            <RevealLine delay={0.4}>tracks the property.</RevealLine>
            <RevealLine delay={0.55}>
              <span className="font-editorial italic font-normal text-sky-600">OPERA360 develops</span>
            </RevealLine>
            <RevealLine delay={0.7}>the people who operate it.</RevealLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Manufactured housing, multifamily, RV resorts, single-family, self-storage, and HOA portfolios run on tribal knowledge,
            spreadsheets, and texts. OPERA360 turns that chaos into one operating standard — training, inspections,
            and audits in a single system.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#demo"
              data-testid="hero-book-demo-cta"
              className="group inline-flex items-center gap-2 rounded-md bg-cadmium px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover hover:-translate-y-0.5 active:scale-[0.98]"
            >
              Book Private Executive Demo
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="#platform"
              data-testid="hero-explore-modules-cta"
              className="inline-flex items-center rounded-md border border-slate-300 px-7 py-3.5 text-sm font-semibold text-slate-900 transition-all duration-200 hover:border-cadmium/60 hover:text-sky-600 hover:-translate-y-0.5 active:scale-[0.98]"
            >
              Explore The 3 Modules
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4 font-mono text-xs text-muted-foreground"
          >
            <span><span className="text-slate-900 font-semibold">TRAIN</span> · WALK · AUDIT</span>
            <span>From <span className="text-sky-600 font-semibold">$299</span>/property/qtr</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 [perspective:1200px]"
        >
          <motion.div
            data-testid="hero-operations-radar"
            onMouseMove={onMove}
            onMouseLeave={() => { mx.set(0); my.set(0); }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="technical-card p-6 sm:p-7 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2.5">
                <Radar size={16} className="text-sky-600" />
                <span className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">
                  Live Operations Radar
                </span>
              </div>
              <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-green-700">
                <span className="h-1.5 w-1.5 rounded-full bg-moss animate-pulse" /> Live
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-7" data-testid="hero-property-type-toggle">
              {Object.keys(RADAR_DATA).map((k) => (
                <button
                  key={k}
                  data-testid={`hero-toggle-${k.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setPtype(k)}
                  className={`rounded-sm px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border transition-all duration-200 ${
                    ptype === k
                      ? "border-cadmium bg-cadmium/10 text-sky-600"
                      : "border-slate-200 text-muted-foreground hover:border-white/25 hover:text-slate-900"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
            <div className="space-y-5">
              <StatBar label="Occupancy" value={d.occupancy} pct={parseFloat(d.occupancy)} />
              <StatBar label="Training Completion" value={d.training} pct={parseFloat(d.training)} />
              <StatBar label="Delinquency" value={d.delinquency} pct={parseFloat(d.delinquency) * 6} accent />
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-3.5">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Make-Ready Avg</p>
                  <p className="mt-1 text-xl font-mono font-semibold text-slate-900">{d.makeReady}</p>
                </div>
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-3.5">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Open Violations</p>
                  <p className="mt-1 text-xl font-mono font-semibold text-sky-600">{d.violations}</p>
                </div>
              </div>
            </div>
            <p className="mt-5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">
              Simulated portfolio telemetry — demo data
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
